import React, { useState, useEffect } from 'react';
import ChatBot from '../components/ChatBot';
import api from '../services/api';
import './PublicHome.css';

function PublicHome() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get('/providers');
      if (response.data.success) {
        setProviders(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-home">
      {/* Hero Section */}
      <header className="hero-section">
        <div className="hero-content">
          <h1 className="clinic-name">Great Falls Medical Center</h1>
          <p className="clinic-tagline">Comprehensive Healthcare for You and Your Family</p>
          <div className="hero-actions">
            <a href="tel:+12404534194" className="btn btn-primary">
              📞 Call Us: (240) 453-4194
            </a>
            <button className="btn btn-secondary" onClick={() => {
              const chatbot = document.querySelector('.chatbot-toggle-btn');
              if (chatbot) chatbot.click();
            }}>
              💬 Chat with AI Assistant
            </button>
          </div>
        </div>
      </header>

      {/* Patient Services Section */}
      <section className="services-section">
        <div className="container">
          <h2>Patient Services</h2>
          <p className="section-subtitle">Manage your healthcare appointments easily</p>
          <div className="services-grid">
            <div className="service-card" onClick={() => {
              const chatbot = document.querySelector('.chatbot-toggle-btn');
              if (chatbot) chatbot.click();
              setTimeout(() => {
                const input = document.querySelector('.chatbot-input');
                if (input) {
                  input.value = 'I would like to schedule a new appointment';
                  input.focus();
                }
              }, 500);
            }}>
              <span className="service-icon">📅</span>
              <h3>Schedule Appointment</h3>
              <p>Book a new appointment with your preferred provider</p>
              <button className="service-btn">Schedule Now</button>
            </div>
            <div className="service-card" onClick={() => {
              const chatbot = document.querySelector('.chatbot-toggle-btn');
              if (chatbot) chatbot.click();
              setTimeout(() => {
                const input = document.querySelector('.chatbot-input');
                if (input) {
                  input.value = 'I need to reschedule my appointment';
                  input.focus();
                }
              }, 500);
            }}>
              <span className="service-icon">🔄</span>
              <h3>Reschedule Appointment</h3>
              <p>Change your existing appointment to a new time</p>
              <button className="service-btn">Reschedule</button>
            </div>
            <div className="service-card" onClick={() => {
              const chatbot = document.querySelector('.chatbot-toggle-btn');
              if (chatbot) chatbot.click();
              setTimeout(() => {
                const input = document.querySelector('.chatbot-input');
                if (input) {
                  input.value = 'I need to cancel my appointment';
                  input.focus();
                }
              }, 500);
            }}>
              <span className="service-icon">❌</span>
              <h3>Cancel Appointment</h3>
              <p>Cancel an upcoming appointment</p>
              <button className="service-btn">Cancel</button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <h2>Welcome to Great Falls Medical Center</h2>
          <p className="about-text">
            At Great Falls Medical Center, we're committed to providing exceptional healthcare services
            to our community. Our team of experienced physicians and staff are here to serve you
            with compassion, expertise, and the latest in medical technology.
          </p>
          <div className="features">
            <div className="feature">
              <span className="feature-icon">🏥</span>
              <h3>Comprehensive Care</h3>
              <p>From family medicine to specialized care</p>
            </div>
            <div className="feature">
              <span className="feature-icon">👨‍⚕️</span>
              <h3>Expert Providers</h3>
              <p>Board-certified physicians in multiple specialties</p>
            </div>
            <div className="feature">
              <span className="feature-icon">📞</span>
              <h3>24/7 Availability</h3>
              <p>Call or chat with our AI assistant anytime</p>
            </div>
            <div className="feature">
              <span className="feature-icon">🤖</span>
              <h3>Smart Scheduling</h3>
              <p>AI-powered appointment booking and management</p>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Section */}
      <section className="providers-section">
        <div className="container">
          <h2>Our Healthcare Providers</h2>
          <p className="section-subtitle">Meet our team of dedicated medical professionals</p>

          {loading ? (
            <div className="loading">Loading providers...</div>
          ) : (
            <div className="providers-grid">
              {providers.map((provider) => (
                <div key={provider.id} className="provider-card">
                  <div className="provider-avatar">
                    {provider.title} {provider.firstName.charAt(0)}{provider.lastName.charAt(0)}
                  </div>
                  <div className="provider-info">
                    <h3>{provider.title} {provider.firstName} {provider.lastName}</h3>
                    <p className="provider-specialty">{provider.specialty}</p>
                    <div className="provider-bio">
                      <p>
                        {provider.title} {provider.lastName} is a board-certified physician
                        specializing in {provider.specialty}. With years of experience,
                        they are committed to providing excellent patient care.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2>Get in Touch</h2>
          <div className="contact-grid">
            <div className="contact-card">
              <span className="contact-icon">📞</span>
              <h3>Phone</h3>
              <p>Call our AI voice assistant 24/7</p>
              <a href="tel:+12404534194" className="contact-link">(240) 453-4194</a>
            </div>
            <div className="contact-card">
              <span className="contact-icon">💬</span>
              <h3>Online Chat</h3>
              <p>Chat with our AI assistant anytime</p>
              <button className="contact-link" onClick={() => {
                const chatbot = document.querySelector('.chatbot-toggle-btn');
                if (chatbot) chatbot.click();
              }}>
                Start Chat
              </button>
            </div>
            <div className="contact-card">
              <span className="contact-icon">📍</span>
              <h3>Location</h3>
              <p>123 Healthcare Drive</p>
              <p>Medical City, ST 12345</p>
            </div>
            <div className="contact-card">
              <span className="contact-icon">⏰</span>
              <h3>Hours</h3>
              <p>Monday - Friday: 8am - 6pm</p>
              <p>Saturday: 9am - 2pm</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Great Falls Medical Center. All rights reserved.</p>
          <p className="footer-links">
            <a href="/admin">Staff Login</a>
          </p>
        </div>
      </footer>

      {/* AI Chatbot */}
      <ChatBot />
    </div>
  );
}

export default PublicHome;
